import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const ackDesktopBodySchema = z.object({
  eventIds: z.array(z.string().min(1)).min(1).max(100),
});

export const ackSuccessSchema = z.object({
  ok: z.literal(true),
});

export function ackNotificationPath(notificationId: string): string {
  return `/api/notifications/${notificationId}/ack`;
}

export function ackDesktopNotificationsPath(): string {
  return "/api/notifications/ack-desktop";
}

async function parseAckSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof ackSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = ackSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function ackNotificationRequest({
  apiBaseUrl,
  accessToken,
  notificationId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  notificationId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof ackSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${ackNotificationPath(notificationId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to acknowledge");
  }

  return parseAckSuccess(response, "Invalid acknowledge response");
}

export async function ackDesktopNotificationsRequest({
  apiBaseUrl,
  accessToken,
  eventIds,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  eventIds: string[];
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof ackSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const body = ackDesktopBodySchema.parse({ eventIds });
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${ackDesktopNotificationsPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to acknowledge desktop notifications",
    );
  }

  return parseAckSuccess(response, "Invalid desktop acknowledge response");
}
